import type { DaySection } from '../api/types'
import type { Vehicle } from './vehicles'

/**
 * The camera script of the day card's film (#90, 1.96.0) -- pure arithmetic,
 * no DOM, so that the Prüfstand can hold it without a browser
 * (`tools/pruefstand/filmscript.ts`).
 *
 * From use, with the example FRA -- New York: "beginnt mit Totalansicht; zoom
 * auf ersten Abschnitt mit Auto (Hotel -> Flughafen); dann Fußweg am
 * Flughafen, dann aufzoomen für den Flug; Schwenk nach Westen, etc." So the
 * film is: the whole day, then one view per section with a flight between
 * them (zoom out, pan, zoom in), and the whole day again at the end.
 *
 * **Within a section the vehicle moves at constant speed along the way**
 * (decided: "gleichmäßig entlang der Strecke"), by distance and not by the
 * clock: a stop of an hour would otherwise be a still picture of an hour's
 * share of the film.
 *
 * **Every section gets at least three seconds, the rest goes by distance.**
 * Otherwise a walk through a terminal beside a flight of 6000 km would last a
 * few frames. The zoom per section is the other half of that answer: the
 * walk fills the picture while it lasts.
 *
 * Positions are Web Mercator, normalised to 0..1 in both axes (`x` east,
 * `y` south) -- the same projection as the tiles, so that a camera is a
 * centre and a zoom and nothing else. A day across the date line is not
 * handled; the camera would fly the long way round.
 */

/** Seconds the whole day stands still before the first section. */
export const INTRO_S = 1.5
/** Seconds the whole day stands still at the end. */
export const OUTRO_S = 1.5
/** The shortest flight between two views, in seconds. */
export const TRANSITION_S = 1.8
/**
 * Seconds per zoom level a flight travels, and the longest flight (1.96.1).
 * From use: "bei einer langen Reise ist das zoomen zu schnell" -- a fixed
 * 1.8 s took the camera from a whole continent down to a terminal, twelve
 * zoom levels, and from a terminal of a few streets to a whole ocean. Each
 * zoom level doubles the scale, so the eye follows a constant number of them
 * per second, not a constant time per flight.
 */
export const SECONDS_PER_ZOOM_LEVEL = 0.45
export const MAX_TRANSITION_S = 5.5
/**
 * How the side view decides which way it faces (1.96.1). From use: "das
 * Fahrzeug wechselt oft die Richtung, ohne dass man das auf der Karte sehen
 * kann (wenn z.B. die Straße eher nach Norden verläuft)". The direction came
 * from the stretch between two points, and a road north wiggles east and west
 * all the time. Now it comes from a window of `FACING_WINDOW` of the section's
 * length, and it only turns when that window goes clearly east or west: its
 * east-west part at least `FACING_SHARE` of its length (within 60 degrees of
 * the horizontal). Northwards the vehicle keeps the way it last faced.
 */
export const FACING_WINDOW = 0.08
export const FACING_SHARE = 0.5
/** The least time a section moves (decided, 5 Oct.). */
export const MIN_SECTION_S = 3
/**
 * The bounds of the length in seconds. They come from the server
 * (`film_length`, `Settings::DARSTELLUNG_ZAHL`) with every wish, not from a
 * copy here (1.121.0): until then they stood here as 5 to 60 a second time,
 * and a wider range on the server would have been cut back in the browser.
 */
export interface FilmLength {
  min: number
  max: number
}

/**
 * How much faster than the others a section may run (1.98.0) -- or slower,
 * a half and a quarter (1.99.0), a tenth and a twentieth (1.109.0) --, as `TrackSections::SPEEDS` holds it. From use: a long flight should run
 * faster than the drive to the airport. The length asked for (fixed or by
 * distance) is the length at normal speed (1.104.0), and a section moves its
 * time at normal speed divided by its speed (1.106.0) -- so a faster section
 * shortens the film and a slower one lengthens it. From use: "Das langsamer
 * scheint aber nicht zu funktionieren", and asked whether a fast flight may
 * then make a short film: "Jede Abweichung ändert". In 1.104.0 the speed
 * still weighted the share of what was left after the minimum per section,
 * and on a short day nothing was left.
 */
export const FILM_SPEEDS = [0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 20, 50, 100, 200, 500] as const

export interface Camera {
  /** The centre, normalised Web Mercator. */
  x: number
  y: number
  /** The zoom, fractional: tiles are 256 px at an integer zoom. */
  zoom: number
}

export interface Frame {
  width: number
  height: number
}

export interface ScriptWish {
  /** `fixed`: the length in `seconds`; `distance`: `rate` seconds per ten kilometres. */
  mode: 'fixed' | 'distance'
  seconds: number
  rate: number
  /**
   * Seconds the vehicle stands at each highlight it passes (1.100.0, from
   * use: "wenn man an einem Highlight vorbeifährt: kurzer Halt"). Only with
   * `pinsWhenPassing`: without it the pins stand from the start and the
   * vehicle passes nothing. Missing means none.
   */
  pause?: number
  /**
   * Seconds the film waits where the vehicle changes (1.150.0, from use:
   * "beim Fahrzeugwechsel kurz warten (einstellbar - Standard 1 s - von 0
   * bis 5 s)"). Between two sections with different vehicles, before the
   * flight into the second: the camera still on the way that ended, the new
   * vehicle at the place of the change. Missing means none.
   */
  change?: number
  /** Highlights appear when the vehicle passes; otherwise they stand from the start. */
  pinsWhenPassing: boolean
  /** The highest zoom the tile server gives. */
  maxZoom: number
  /**
   * The bounds of the length: the least from the server, the most the
   * card's own "Höchstens" within the server's.
   */
  length: FilmLength
  /** The server's longest film (`film_length.max`): "länger" goes no further. Missing is `length.max`. */
  ceiling?: number
  /**
   * What happens when the film as asked for is longer than its limit
   * (1.149.0, `FilmTiming`): `longer` keeps it as it is, `fit` shortens
   * everything alike to the limit. Asked in the sheet, never decided here;
   * missing is `fit`, the way a film was cut before.
   */
  fit?: FilmFit | null
  /**
   * Picture points covered at the bottom (1.148.0): the strip of the height
   * profile. The views are fitted above it; missing means none.
   */
  bottom?: number
}

/** The answer to "too long" (1.149.0): keep the film longer, or fit it. */
export type FilmFit = 'longer' | 'fit'

/**
 * The length of a film against its limit (1.149.0). From use: "solange die
 * Filmdauer nicht überschritten wird: keine Anpassungen an der
 * Geschwindigkeit etc.; falls der Film zu lang wird: Abfrage: Film länger
 * oder dynamische Anpassungen", decided: the limit is the set length under
 * "Feste Länge" and "Höchstens" under "Nach Strecke", and "dynamisch" shortens
 * everything alike.
 */
export interface FilmTiming {
  /** The film as asked for: stops full, flights their own length, every section at its speed. */
  natural: number
  /** The limit it is held against. */
  limit: number
  /** Whether it is longer than the limit -- then the sheet asks. */
  over: boolean
  /** How long "länger" makes it: the natural length, at most the next bound. */
  longer: number
}

export interface Pin {
  lat: number
  lon: number
  title: string
  at?: string | null
  /** The point's little picture (1.99.0): the film shows it like the map's pin. */
  image_url?: string | null
}

/** One section as the film needs it: points in Mercator, distances summed. */
interface Leg {
  vehicle: Vehicle
  /** Mercator points. */
  xy: [number, number][]
  /** Metres from the start of the section to each point. */
  sofar: number[]
  length: number
  /** How fast the section plays (1.98.0): its time at normal speed is divided by it. */
  speed: number
  camera: Camera
  /** Per stretch (index of its end point): whether the side view faces west, and the heading. */
  facing: boolean[]
  bearing: number[]
  /** When the movement starts and ends, in seconds of the film. */
  start: number
  end: number
}

export interface VehicleState {
  vehicle: Vehicle
  x: number
  y: number
  /** True when the way goes west: a side view is mirrored. */
  westward: boolean
  /** The direction of travel in the frame, radians from east, clockwise (`y` points south): a top view turns to it. */
  heading: number
  /** Its section (index into `lines`) and metres from the section's start (1.143.0): the 3D vehicle finds the way behind it there. */
  leg: number
  along: number
}

export interface ScriptState {
  camera: Camera
  /** Per section: how much of its line is drawn, 0..1. */
  drawn: number[]
  /** `null` before anything has a vehicle (never, with at least one section). */
  vehicle: VehicleState | null
  /** Indices into the pins that stand. */
  pins: number[]
  /**
   * Seconds the vehicle has moved so far (1.150.1): the clock of turning
   * wheels and walking legs. It stands still wherever the vehicle does --
   * before the first section, in a flight, at a stop, at a change of
   * vehicle. From use: "das Problem scheint beim Übergang von und zu
   * Fußgängern zu sein" -- on the film's clock the walker walked on the
   * spot through every wait, and the wait looked like none.
   */
  moving: number
}

export interface FilmScript {
  /** Length of the film in seconds. */
  duration: number
  /** Its length as asked for against its limit (1.149.0). */
  timing: FilmTiming
  /** The still frames before the first section and after the flight back -- `INTRO_S` and `OUTRO_S`, shortened with the film when it is fitted (1.149.0). */
  intro: number
  outro: number
  /**
   * Per section, the wait before the flight into it (1.150.0): the stop at a
   * change of vehicle, 0 for the first section and where the vehicle stays.
   * The flight begins after it; `tourScript()` reads it here, so that the
   * following camera does not fly while the film waits.
   */
  holds: number[]
  /** The sections' lines in Mercator, for the renderer. */
  lines: { vehicle: Vehicle; xy: [number, number][]; sofar: number[]; length: number }[]
  /** The pins in Mercator, in the order given. */
  pins: { x: number; y: number; title: string; image: string | null }[]
  /** The camera of the whole day: the poster and the first and last frame. */
  whole: Camera
  at(t: number): ScriptState
}

const TILE = 256
const EARTH_RADIUS = 6371000

export function mercator(lat: number, lon: number): [number, number] {
  const phi = (Math.max(-85.05, Math.min(85.05, lat)) * Math.PI) / 180
  return [(lon + 180) / 360, (1 - Math.log(Math.tan(phi) + 1 / Math.cos(phi)) / Math.PI) / 2]
}

function metres(a: [number, number, number], b: [number, number, number]): number {
  const r = Math.PI / 180
  const dLat = (b[0] - a[0]) * r
  const dLon = (b[1] - a[1]) * r
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a[0] * r) * Math.cos(b[0] * r) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS * Math.asin(Math.min(1, Math.sqrt(h)))
}

/**
 * The camera that holds a set of Mercator points, with `margin` pixels free
 * on every side. A single point (or a walk of a few metres) gets the highest
 * zoom, not an infinite one.
 *
 * `bottom` pixels more stay free at the bottom (1.148.0), and the points sit
 * in the middle of what is left. From use, with a film: "die Endposition wird
 * durch das Höhenprofil verdeckt" -- the whole day was fitted to the full
 * picture, and its southern end lay under the profile's strip.
 */
export function fit(xy: [number, number][], frame: Frame, maxZoom: number, margin = 48, bottom = 0): Camera {
  let x0 = Infinity
  let y0 = Infinity
  let x1 = -Infinity
  let y1 = -Infinity
  for (const [x, y] of xy) {
    x0 = Math.min(x0, x)
    y0 = Math.min(y0, y)
    x1 = Math.max(x1, x)
    y1 = Math.max(y1, y)
  }
  if (!Number.isFinite(x0)) return { x: 0.5, y: 0.5, zoom: 1 }
  const free = { w: Math.max(1, frame.width - 2 * margin), h: Math.max(1, frame.height - 2 * margin - bottom) }
  const zx = x1 > x0 ? Math.log2(free.w / ((x1 - x0) * TILE)) : maxZoom
  const zy = y1 > y0 ? Math.log2(free.h / ((y1 - y0) * TILE)) : maxZoom
  const zoom = Math.max(0, Math.min(maxZoom, zx, zy))
  // The camera looks half the covered strip further south, so the points
  // stand in the middle of the free part above it.
  return { x: (x0 + x1) / 2, y: (y0 + y1) / 2 + bottom / 2 / (TILE * 2 ** zoom), zoom }
}

/**
 * The length of the film the setting asks for at normal speed, before the
 * sections claim their minimum. The speeds come after it (1.106.0).
 *
 * Not held under "Höchstens" any more (1.149.0): a long day "Nach Strecke"
 * asked for more than the most and got the most without a word. Now it is
 * what it asks for, and `FilmTiming` says whether that is over the limit.
 * The set length under "Feste Länge" stays within its bounds, as the field
 * holds it.
 */
export function wishedDuration(wish: Pick<ScriptWish, 'mode' | 'seconds' | 'rate' | 'length'>, metres: number): number {
  // The rate is bounded by the server (`Settings`, 1.123.0), not here a second time.
  const rate = Math.max(0, wish.rate)
  if (wish.mode === 'distance') return Math.max(wish.length.min, (metres / 10_000) * rate)
  return Math.min(wish.length.max, Math.max(wish.length.min, wish.seconds))
}

export function smooth(u: number): number {
  const v = Math.max(0, Math.min(1, u))
  return v * v * (3 - 2 * v)
}

/** Two cameras close enough that a flight between them would only shake the picture. */
function nearlySame(a: Camera, b: Camera, frame: Frame): boolean {
  const scale = TILE * 2 ** Math.min(a.zoom, b.zoom)
  return Math.abs(a.zoom - b.zoom) < 0.35
    && Math.abs(a.x - b.x) * scale < frame.width * 0.08
    && Math.abs(a.y - b.y) * scale < frame.height * 0.08
}

/**
 * The balance of zooming and panning on a flight (van Wijk and Nuij), the
 * value d3 and Leaflet's `flyTo` use.
 */
const RHO = Math.SQRT2

/**
 * A flight from one camera to another along the shortest path in zoom and
 * pan (van Wijk and Nuij, "Smooth and efficient zooming and panning", 2003).
 *
 * Until 1.97.1 the centre moved evenly over the map while the zoom moved
 * evenly in levels. From the whole of a trip to Frankfurt that put the camera
 * at village level halfway across the Atlantic, and it then crept over
 * Brittany and Normandy to the start -- from use: "der Zoom am Anfang ist
 * immer noch verwirrend". On this path the camera pans while it is far out
 * and zooms in when the target is below it; between two close-ups far apart
 * it zooms out by itself, so there is no separate dip any more.
 *
 * `length` is the length of the path in zoom levels: a pure zoom of n levels
 * is n, panning adds to it.
 */
function flightPath(a: Camera, b: Camera, frame: Frame): { length: number; at: (t: number) => Camera } {
  const size = Math.max(frame.width, frame.height) / TILE
  const w0 = size / 2 ** a.zoom
  const w1 = size / 2 ** b.zoom
  const dx = b.x - a.x
  const dy = b.y - a.y
  const d = Math.hypot(dx, dy)
  const rho2 = RHO * RHO
  if (d * 1e9 < Math.min(w0, w1)) {
    return {
      length: Math.abs(b.zoom - a.zoom),
      at: (t) => ({ x: a.x + dx * t, y: a.y + dy * t, zoom: a.zoom + (b.zoom - a.zoom) * t }),
    }
  }
  // log(sqrt(b^2 + 1) - b) is -asinh(b), and asinh keeps its precision
  // where the difference would cancel to nothing.
  const r0 = -Math.asinh((w1 * w1 - w0 * w0 + rho2 * rho2 * d * d) / (2 * w0 * rho2 * d))
  const r1 = -Math.asinh((w1 * w1 - w0 * w0 - rho2 * rho2 * d * d) / (2 * w1 * rho2 * d))
  const S = (r1 - r0) / RHO
  return {
    length: (S * RHO) / Math.LN2,
    at: (t) => {
      if (t >= 1) return b
      const s = t * S
      const u = (w0 / (rho2 * d)) * (Math.cosh(r0) * Math.tanh(RHO * s + r0) - Math.sinh(r0))
      const w = (w0 * Math.cosh(r0)) / Math.cosh(RHO * s + r0)
      return { x: a.x + dx * u, y: a.y + dy * u, zoom: Math.log2(size / w) }
    },
  }
}

/** The camera at share `u` of a flight, eased at both ends. */
export function flight(a: Camera, b: Camera, u: number, frame: Frame, maxZoom: number): Camera {
  const camera = flightPath(a, b, frame).at(smooth(u))
  return { ...camera, zoom: Math.max(0, Math.min(maxZoom, camera.zoom)) }
}

/** The point at `m` metres along a section, clamped to its ends. */
function pointAt(xy: [number, number][], sofar: number[], m: number): [number, number] {
  const length = sofar[sofar.length - 1]!
  const target = Math.max(0, Math.min(length, m))
  let lo = 1
  let hi = sofar.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (sofar[mid]! < target) lo = mid + 1
    else hi = mid
  }
  const a = xy[lo - 1]!
  const b = xy[lo]!
  const from = sofar[lo - 1]!
  const to = sofar[lo]!
  const u = to > from ? (target - from) / (to - from) : 0
  return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u]
}

/**
 * Which way the vehicle faces on each stretch of a section, and its heading
 * (see `FACING_WINDOW`). The window is centred on the stretch, so a turn is
 * seen a little before the vehicle reaches it, as a driver would.
 */
function facingOf(xy: [number, number][], sofar: number[]): { facing: boolean[]; bearing: number[] } {
  const length = sofar[sofar.length - 1]!
  const half = (length * FACING_WINDOW) / 2
  const windows: { dx: number; dy: number }[] = [{ dx: 0, dy: 0 }]
  for (let i = 1; i < xy.length; i++) {
    const mid = (sofar[i - 1]! + sofar[i]!) / 2
    const a = pointAt(xy, sofar, mid - half)
    const b = pointAt(xy, sofar, mid + half)
    windows.push({ dx: b[0] - a[0], dy: b[1] - a[1] })
  }
  const clear = (w: { dx: number; dy: number }) => {
    const size = Math.hypot(w.dx, w.dy)
    return size > 0 && Math.abs(w.dx) >= FACING_SHARE * size
  }
  // Before the first clear window: the way that window will go, or the
  // section as a whole, or east.
  const first = windows.find((w, i) => i > 0 && clear(w))
  const whole = xy[xy.length - 1]![0] - xy[0]![0]
  let west = first !== undefined ? first.dx < 0 : whole < 0
  let heading = 0
  const facing = [west]
  const bearing = [heading]
  for (let i = 1; i < windows.length; i++) {
    const w = windows[i]!
    if (clear(w)) west = w.dx < 0
    if (w.dx !== 0 || w.dy !== 0) heading = Math.atan2(w.dy, w.dx)
    facing.push(west)
    bearing.push(heading)
  }
  // A heading before the first movement is the first one there is.
  const firstHeading = windows.findIndex((w, i) => i > 0 && (w.dx !== 0 || w.dy !== 0))
  if (firstHeading > 0) for (let i = 0; i < firstHeading; i++) bearing[i] = bearing[firstHeading]!
  return { facing, bearing }
}

/** Where along a section the vehicle is at a share of its length, and which way it goes. */
function alongLeg(leg: Leg, share: number): { x: number; y: number; westward: boolean; heading: number; along: number } {
  const target = share * leg.length
  let i = 1
  while (i < leg.sofar.length - 1 && leg.sofar[i]! < target) i++
  const a = leg.xy[i - 1]!
  const b = leg.xy[i]!
  const from = leg.sofar[i - 1]!
  const to = leg.sofar[i]!
  const u = to > from ? (target - from) / (to - from) : 0
  return { x: a[0] + (b[0] - a[0]) * u, y: a[1] + (b[1] - a[1]) * u, westward: leg.facing[i]!, heading: leg.bearing[i]!, along: target }
}

/** How long the flight between two views takes: by the length of its path in zoom levels. */
function transitionSeconds(a: Camera, b: Camera, frame: Frame): number {
  if (nearlySame(a, b, frame)) return 0
  const levels = flightPath(a, b, frame).length
  return Math.min(MAX_TRANSITION_S, Math.max(TRANSITION_S, levels * SECONDS_PER_ZOOM_LEVEL))
}

/**
 * The script for a day. `null` when there is nothing to move: no section
 * with two points.
 */
export function filmScript(
  sections: DaySection[],
  rawPins: Pin[],
  frame: Frame,
  wish: ScriptWish,
): FilmScript | null {
  const legs: Leg[] = []
  for (const s of sections) {
    if (s.points.length < 2) continue
    const sofar = [0]
    for (let i = 1; i < s.points.length; i++) sofar.push(sofar[i - 1]! + metres(s.points[i - 1]!, s.points[i]!))
    const xy = s.points.map(([lat, lon]) => mercator(lat, lon))
    const length = sofar[sofar.length - 1]!
    legs.push({
      vehicle: s.vehicle,
      xy,
      sofar,
      length,
      // Not `Math.max(1, …)`: a speed under one is a slower section (1.99.0).
      speed: (s.speed ?? 1) > 0 ? (s.speed ?? 1) : 1,
      camera: fit(xy, frame, wish.maxZoom, undefined, wish.bottom ?? 0),
      ...facingOf(xy, sofar),
      start: 0,
      end: 0,
    })
  }
  if (legs.length === 0) return null

  const pins = rawPins.map((p) => {
    const [x, y] = mercator(p.lat, p.lon)
    return { x, y, title: p.title, image: p.image_url ?? null }
  })
  const whole = fit([...legs.flatMap((l) => l.xy), ...pins.map((p): [number, number] => [p.x, p.y])], frame, wish.maxZoom, undefined, wish.bottom ?? 0)

  // The flights: into each section from the view before, and back to the whole day.
  const transitions: number[] = []
  let before = whole
  for (const leg of legs) {
    transitions.push(transitionSeconds(before, leg.camera, frame))
    before = leg.camera
  }
  const flightBack = transitionSeconds(before, whole, frame)

  // Everything up to the sections' own times is planned at normal speed:
  // the speeds change how long a section moves and nothing else -- not the
  // flights, not the stops (1.106.0).
  const totalLength = legs.reduce((a, l) => a + l.length, 0)
  const wished = wishedDuration(wish, totalLength)
  // The flights at their own length (1.149.0): until 1.148.1 they together
  // took at most 40 % of the wished length and were shortened without a word
  // (`TRANSITION_SHARE`). A film too long for its limit is now a question in
  // the sheet, and "anpassen" shortens them with everything else.

  // The stops (1.100.0): one at each pin whose moment lies inside a section
  // -- the ones the vehicle really passes -- each as long as asked for
  // (1.149.0). Until 1.148.1 they together took at most 30 % of the film and
  // were shortened alike without a word; from use: "ich hatte 3 s, aber er
  // hält keine 3 sec an". They take their time out of the length asked for,
  // like the flights: under "Feste Länge" the film stays its length while it
  // fits.
  const asked = wish.pinsWhenPassing ? Math.max(0, wish.pause ?? 0) : 0
  const passed = asked > 0
    ? rawPins.filter((p) => {
      const at = p.at ? Date.parse(p.at) : NaN
      return !Number.isNaN(at) && sections.some((s) => s.points.length >= 2 && at >= Date.parse(s.from) && at <= Date.parse(s.to))
    }).length
    : 0

  // The waits at a change of vehicle (1.150.0): one before each section
  // whose vehicle differs from the one before, each as long as asked for.
  // Like the stops they take their time out of the length asked for, and
  // only a film over its limit shortens them.
  const askedChange = Math.max(0, wish.change ?? 0)
  const changes = legs.map((leg, i) => i > 0 && leg.vehicle !== legs[i - 1]!.vehicle)
  const changeCount = changes.filter(Boolean).length

  const flights = flightBack + transitions.reduce((a, b) => a + b, 0)
  const fixed = INTRO_S + OUTRO_S + flights + passed * asked + changeCount * askedChange
  const moving = Math.max(wished - fixed, MIN_SECTION_S * legs.length)
  const rest = moving - MIN_SECTION_S * legs.length

  // A section's time at normal speed, divided by its speed (1.106.0). From
  // use, with a video: a bike at a quarter of the speed moved its three
  // seconds like the others, and the film grew by five seconds of longer
  // stops. The speed only weighted the share of the rest, and on a short day
  // there was no rest: every section stood at its minimum. Divided, a quarter
  // of the speed is four times the time, whatever the rest. A faster section
  // keeps its minimum, or a flight at 200x would be a single frame.
  const normal = legs.map((leg) => MIN_SECTION_S + rest * (totalLength > 0 ? leg.length / totalLength : 1 / legs.length))
  const times = legs.map((leg, i) => (leg.speed < 1 ? normal[i]! / leg.speed : Math.max(MIN_SECTION_S, normal[i]! / leg.speed)))

  // The film as asked for, against its limit (1.149.0, `FilmTiming`): the
  // set length under "Feste Länge", "Höchstens" under "Nach Strecke". Within
  // it nothing is changed. Over it, `fit` shortens the stops and the sections
  // alike, by one factor -- so a slow section stays the slow one -- a section
  // not under `MIN_SECTION_S`. **The flights keep their length**, and so do
  // the still frames at both ends: decided was "alles gleichmäßig", and
  // measured in `filmscript.ts` the flights shortened alike made the camera
  // turn 37 zoom levels a second (a day of 620 s fitted to 60), and still 10
  // with a floor of `TRANSITION_S`; at their own length it is under 4. A
  // flight is a move across so many zoom levels, and any less time for it is
  // a faster camera. `longer` keeps the film, up to the next bound
  // ("Höchstens" under "Feste Länge", the server's under "Nach Strecke"),
  // and over that it is fitted the same way. Where even the least lengths
  // are too long, the film is as short as they allow.
  const natural = fixed + times.reduce((a, b) => a + b, 0)
  const limit = wish.mode === 'fixed' ? wished : wish.length.max
  const bound = wish.mode === 'fixed' ? wish.length.max : Math.max(wish.length.max, wish.ceiling ?? wish.length.max)
  const over = natural > limit + 1e-9
  const longer = Math.min(natural, bound)
  const target = !over ? natural : wish.fit === 'longer' ? longer : limit
  // The waits at a change of vehicle are stops as well, and go alike.
  const shortened = (k: number) => ({
    sections: times.map((d) => Math.max(Math.min(d, MIN_SECTION_S), d * k)),
    pause: asked * k,
    change: askedChange * k,
  })
  const lengthOf = (k: number) => {
    const p = shortened(k)
    return INTRO_S + OUTRO_S + flights + p.sections.reduce((a, b) => a + b, 0) + passed * p.pause + changeCount * p.change
  }
  let k = 1
  if (target < natural - 1e-9) {
    if (lengthOf(0) >= target) {
      k = 0
    } else {
      let lo = 0
      let hi = 1
      for (let n = 0; n < 60; n++) {
        const mid = (lo + hi) / 2
        if (lengthOf(mid) > target) hi = mid
        else lo = mid
      }
      k = lo
    }
  }
  const final = shortened(k)
  const timing: FilmTiming = { natural, limit, over, longer }
  const intro = INTRO_S
  const outro = OUTRO_S
  const pause = final.pause
  final.sections.forEach((seconds, i) => { times[i] = seconds })
  const squeeze = 1

  // The wait at a change lies in the driving time, before the flight: the
  // previous section has ended (its line drawn), the next has not begun, so
  // `at()` shows the new vehicle at its start under the previous camera.
  const holds = changes.map((changed) => (changed ? final.change : 0))
  let t = intro
  legs.forEach((leg, i) => {
    t += holds[i]!
    t += transitions[i]!
    leg.start = t
    t += times[i]! * squeeze
    leg.end = t
  })
  const flightBackStart = t
  // The film without its stops: everything below is computed in this
  // driving time, and `at()` turns the film's time into it.
  const driving = t + flightBack + outro

  // When each pin appears, in driving time: when the vehicle passes its
  // moment; a moment outside every section appears with the section that
  // follows it (or at the end). `stops` marks the ones passed inside a
  // section -- there the vehicle stands.
  const stops = new Set<number>()
  const appears = rawPins.map((p, index) => {
    if (!wish.pinsWhenPassing) return 0
    const at = p.at ? Date.parse(p.at) : NaN
    if (Number.isNaN(at)) return 0
    let k = 0
    for (const s of sections) {
      if (s.points.length < 2) continue
      const leg = legs[k++]!
      const from = Date.parse(s.from)
      const to = Date.parse(s.to)
      if (at < from) return leg.start
      if (at <= to) {
        const second = (at - from) / 1000
        let nearest = 0
        for (let i = 1; i < s.points.length; i++) {
          if (Math.abs(s.points[i]![2] - second) < Math.abs(s.points[nearest]![2] - second)) nearest = i
        }
        const share = leg.length > 0 ? leg.sofar[nearest]! / leg.length : 0
        if (pause > 0) stops.add(index)
        return leg.start + (leg.end - leg.start) * share
      }
    }
    return flightBackStart
  })

  // The stops on the film's time line: the k-th (by driving time) begins at
  // its driving time plus the k stops before it.
  const stopsAt = [...stops].map((i) => appears[i]!).sort((a, b) => a - b)
  const duration = driving + stopsAt.length * pause
  const filmTimeOf = (d: number) => d + pause * stopsAt.filter((s) => s < d).length
  const drivingTimeOf = (time: number) => {
    for (let k = 0; k < stopsAt.length; k++) {
      const begins = stopsAt[k]! + k * pause
      if (time < begins) return time - k * pause
      if (time < begins + pause) return stopsAt[k]!
    }
    return time - stopsAt.length * pause
  }
  const shows = appears.map((d) => filmTimeOf(d))

  const at = (time: number): ScriptState => {
    const t = drivingTimeOf(Math.max(0, Math.min(duration, time)))
    const drawn = legs.map((l) => (t >= l.end ? 1 : t <= l.start ? 0 : (t - l.start) / (l.end - l.start)))

    let camera: Camera = whole
    let vehicle: VehicleState | null = null
    if (t >= flightBackStart) {
      const last = legs[legs.length - 1]!
      camera = flightBack > 0 ? flight(last.camera, whole, (t - flightBackStart) / flightBack, frame, wish.maxZoom) : whole
      vehicle = { vehicle: last.vehicle, leg: legs.length - 1, ...alongLeg(last, 1) }
    } else {
      let i = legs.findIndex((l) => t < l.end)
      if (i < 0) i = legs.length - 1
      const leg = legs[i]!
      const previous = i === 0 ? whole : legs[i - 1]!.camera
      const flightStart = leg.start - transitions[i]!
      if (t < flightStart) {
        camera = previous
      } else if (t < leg.start) {
        camera = flight(previous, leg.camera, (t - flightStart) / transitions[i]!, frame, wish.maxZoom)
      } else {
        camera = leg.camera
      }
      vehicle = { vehicle: leg.vehicle, leg: i, ...alongLeg(leg, drawn[i]!) }
    }

    // Driving time inside the sections only: the stops at highlights are
    // already out of it (`drivingTimeOf()`), the waits and flights between
    // the sections lie outside every section.
    const moving = legs.reduce((sum, l) => sum + Math.max(0, Math.min(t, l.end) - l.start), 0)

    const visible: number[] = []
    const now = Math.max(0, Math.min(duration, time))
    shows.forEach((from, i) => {
      if (now >= from) visible.push(i)
    })
    return { camera, drawn, vehicle, pins: visible, moving }
  }

  return {
    duration,
    timing,
    intro,
    outro,
    holds,
    lines: legs.map((l) => ({ vehicle: l.vehicle, xy: l.xy, sofar: l.sofar, length: l.length })),
    pins,
    whole,
    at,
  }
}

/** Mercator to the pixel of a frame, for a camera. */
export function toFrame(x: number, y: number, camera: Camera, frame: Frame): [number, number] {
  const scale = TILE * 2 ** camera.zoom
  return [(x - camera.x) * scale + frame.width / 2, (y - camera.y) * scale + frame.height / 2]
}

/**
 * The tiles a camera needs: at the integer zoom nearest to its own, scaled by
 * the rest. Rounded and not floored: a tile drawn at 0.7 to 1.4 of its size
 * stays sharp enough, and one level fewer would halve the detail.
 *
 * `coarser` takes levels away when a film would need too many tiles: a
 * quarter of the tiles per level, at the price of a softer picture.
 */
export function tilesFor(camera: Camera, frame: Frame, maxZoom: number, coarser = 0): { z: number; x0: number; x1: number; y0: number; y1: number } {
  const z = Math.max(0, Math.min(maxZoom, Math.round(camera.zoom)) - coarser)
  const n = 2 ** z
  const scale = TILE * 2 ** camera.zoom
  const halfW = frame.width / 2 / scale
  const halfH = frame.height / 2 / scale
  return {
    z,
    x0: Math.floor((camera.x - halfW) * n),
    x1: Math.floor((camera.x + halfW) * n),
    y0: Math.max(0, Math.floor((camera.y - halfH) * n)),
    y1: Math.min(n - 1, Math.floor((camera.y + halfH) * n)),
  }
}
